import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { ArticlePage } from "@/components/marketing/article-page";
import {
  BLOG_IMAGE_HEIGHT,
  BLOG_IMAGE_WIDTH,
  blogSocialImagePath,
} from "@/components/marketing/blog-images";
import { NotFoundPage } from "@/components/marketing/not-found-page";
import { MARKETING_FLAGS } from "@/lib/marketing-flags";
import { articleJsonLd, blogBreadcrumbJsonLd, createPageHead } from "@/lib/seo";

/**
 * The post comes from the loader rather than a module-scope import, so the blog
 * corpus does not ride along in the app's entry chunk. See `help.$slug`.
 */
export const Route = createFileRoute("/(public)/blog/$slug")({
  beforeLoad: () => {
    if (!MARKETING_FLAGS.blog) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ params }) => {
    const { getPost } = await import("@/components/marketing/blog-data");
    const post = getPost(params.slug);

    // Throwing rather than rendering a not-found body at 200: an unknown slug
    // used to answer OK with the not-found page, which Search Console reports
    // as a soft 404 and which wastes a crawl on a URL that does not exist.
    // Drafts live behind this too, so it is also what keeps an unpublished
    // post from being served.
    if (!post) {
      throw notFound();
    }

    return { post };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;

    // `notFound()` in the loader means `head` still runs with no data on the
    // way to the not-found component; the root's own head covers that case.
    if (!post) {
      return {};
    }

    return createPageHead(
      {
        path: `/blog/${post.slug}`,
        title: `${post.title} — PostDaily`,
        description: post.excerpt,
        ogType: "article",
      },
      {
        image: blogSocialImagePath(post.slug),
        imageAlt: post.imageAlt,
        imageHeight: BLOG_IMAGE_HEIGHT,
        imageWidth: BLOG_IMAGE_WIDTH,
        jsonLd: [articleJsonLd(post), blogBreadcrumbJsonLd(post)],
      },
    );
  },
  notFoundComponent: NotFoundPage,
  component: ArticleRoute,
});

function ArticleRoute() {
  const { post } = Route.useLoaderData();

  return <ArticlePage post={post} />;
}
